import { Link } from "react-router-dom";
import ServiceLayout, { ContentSection } from "@/components/site/ServiceLayout";
import { Reveal, TickList } from "@/components/site/Reveal";

const HELP_WITH = [
  "Big worries and anxiety",
  "Trauma or frightening experiences",
  "Grief and loss",
  "Family change and separation",
  "Emotional outbursts and regulation",
  "Social and friendship difficulties",
  "Neurodivergence",
  "Adjustment to illness, injury or disability",
];

const SESSIONS = [
  {
    title: "Child-led play",
    text: "Toys, art, sandtray, games and video-game-informed play give children a natural way to express what words cannot.",
  },
  {
    title: "Parents as partners",
    text: "Regular check-ins keep you involved, with practical strategies to support your child between sessions.",
  },
  {
    title: "A gentle pace",
    text: "Children set the pace. Safety and trust come first — therapy never pushes a child faster than they are ready for.",
  },
];

export default function PlayTherapy() {
  return (
    <ServiceLayout
      eyebrow="Services"
      title="Play Therapy"
      lead="Play is a child's natural language. Play therapy gives children a safe, creative way to express feelings, process experiences and build emotional skills."
      image="/images/play-therapy.jpg"
      imageAlt="Children's hands drawing in green sensory sand"
    >
      <ContentSection eyebrow="Who this helps" title="We may be able to help children with">
        <TickList items={HELP_WITH} />
      </ContentSection>

      <ContentSection eyebrow="What sessions look like" title="Therapy that speaks a child's language" pale>
        <div className="grid gap-6 md:grid-cols-3">
          {SESSIONS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-euca-line bg-white p-7 shadow-sm">
                <span className="font-serif text-3xl italic text-euca-sage/70">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-serif text-xl font-medium text-euca-dark">{s.title}</h3>
                <p className="mt-2 text-base leading-relaxed text-mutedsoft">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </ContentSection>

      <ContentSection eyebrow="Fees & funding" title="Three ways to fund play therapy">
        <Reveal>
          <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-mutedsoft">
            Play therapy is <span className="font-semibold text-charcoal">$200 per 50-minute session</span>, and can
            be funded privately, with a <span className="font-semibold text-charcoal">Medicare rebate of $89.50</span>{" "}
            when your child has a Mental Health Treatment Plan from their GP, or through{" "}
            <span className="font-semibold text-charcoal">NDIS funding</span> (self-managed or plan-managed) where
            therapy is included in your child's plan. See{" "}
            <Link to="/fees-funding" className="font-semibold text-euca hover:underline" data-testid="play-fees-link">
              Fees &amp; Funding
            </Link>{" "}
            for details.
          </p>
        </Reveal>
      </ContentSection>
    </ServiceLayout>
  );
}
